import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { apiErrorMessage, toDisplayError } from '../utils/apiError';
import { useSingleUseToken } from '../utils/singleUseToken';

type VerificationStatus = 'loading' | 'success' | 'error' | 'no-token';

function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // REV-878: the email links to /verify-email#token=…; the hook reads the
  // fragment (or the older ?token=) and strips it from the URL. An old
  // /verify-email/:token link is turned into the fragment form by
  // LegacyTokenRedirect before this page loads.
  const token = useSingleUseToken();

  const [status, setStatus] = useState<VerificationStatus>(token ? 'loading' : 'no-token');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    // Guard against React StrictMode's double-invocation: refs persist across
    // the simulated unmount/remount so only the first invocation proceeds.
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyEmail = async () => {
      try {
        await apiClient.post('/v1/auth/verify-email', { token });
        setStatus('success');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          void navigate('/login', {
            state: { message: t('auth.verifyEmail.successMessage') },
          });
        }, 3000);
      } catch (error: unknown) {
        setStatus('error');

        // The envelope is not uniform — `error` is a string on some endpoints
        // and { message, code } on others. Calling .includes() straight on it
        // threw "…includes is not a function" for the object form, breaking the
        // page instead of showing a message (REV-509).
        const apiMsg = apiErrorMessage(error);

        if (apiMsg?.includes('expired')) {
          setErrorMessage(t('auth.errors.verificationExpired'));
        } else if (apiMsg?.includes('invalid')) {
          setErrorMessage(t('auth.errors.verificationInvalid'));
        } else if (apiMsg?.includes('already verified')) {
          setErrorMessage(t('auth.errors.alreadyVerified'));
        } else {
          setErrorMessage(toDisplayError(error, t('auth.errors.verificationFailed')));
        }
      }
    };

    void verifyEmail();
  }, [token, navigate, t]);

  // No token provided
  if (status === 'no-token') {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('auth.verifyEmail.invalidLink')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('auth.verifyEmail.invalidLinkMessage')}
        </Typography>
        <Button component={RouterLink} to="/login" variant="contained">
          {t('auth.verifyEmail.backToLogin')}
        </Button>
      </Box>
    );
  }

  // Loading state
  if (status === 'loading') {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('auth.verifyEmail.verifying')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.verifyEmail.pleaseWait')}
        </Typography>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
          {t('auth.verifyEmail.successTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('auth.verifyEmail.successMessage')}
        </Typography>
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {t('auth.verifyEmail.redirecting')}
        </Typography>
      </Box>
    );
  }

  // Error state
  return (
    <Box sx={{ textAlign: 'center' }}>
      <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
      <Typography variant="h5" component="h1" gutterBottom fontWeight={700}>
        {t('auth.verifyEmail.errorTitle')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {errorMessage}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button component={RouterLink} to="/login" variant="outlined">
          {t('auth.verifyEmail.backToLogin')}
        </Button>
        <Button component={RouterLink} to="/resend-verification" variant="contained">
          {t('auth.verifyEmail.resendLink')}
        </Button>
      </Box>
    </Box>
  );
}

export default VerifyEmailPage;
